import { ArrowRightIcon, ShieldIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatMoney } from "@/helpers/format"
import type { SmartRoutingSecondary } from "@/types/smart-routing"

interface SecondaryGatewayProps {
  secondary: SmartRoutingSecondary
  primaryProgress: number | null
  className?: string
}

export const SecondaryGateway = ({
  secondary,
  primaryProgress,
  className,
}: SecondaryGatewayProps) => {
  const { t } = useTranslation()

  if (!secondary) {
    return (
      <div className={className}>
        <span className="text-sm text-muted-foreground">
          {t("smartRouting.secondary.notConfigured")}
        </span>
      </div>
    )
  }

  const isSecondaryActive =
    primaryProgress !== null && primaryProgress >= 100
  const targetFormatted = secondary.target
    ? formatMoney(secondary.target / 100)
    : "—"

  return (
    <div className={className}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex cursor-help items-center gap-2">
            {isSecondaryActive && (
              <ArrowRightIcon
                size={14}
                className="text-amber-600"
              />
            )}

            <div className="flex items-center gap-1.5">
              <ShieldIcon
                size={14}
                className="text-muted-foreground"
              />
              <span
                className={`text-sm font-medium ${
                  isSecondaryActive
                    ? "text-amber-700"
                    : "text-muted-foreground"
                }`}
              >
                {secondary.processor}
              </span>
            </div>

            {isSecondaryActive && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                {t("smartRouting.secondary.active")}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent
          style={{
            backgroundColor: "var(--whitelabel-background)",
          }}
          className="border-0 text-white"
        >
          <div className="space-y-1">
            <p className="font-medium text-white">
              {t("smartRouting.secondary.title")}
            </p>
            <p className="text-sm text-white">
              {t("smartRouting.secondary.processor")}:{" "}
              {secondary.processor}
            </p>
            {secondary.target && (
              <p className="text-sm text-white">
                {t("smartRouting.secondary.target")}:{" "}
                {targetFormatted}
              </p>
            )}
            {isSecondaryActive ? (
              <p className="text-xs text-gray-200">
                ⚠️{" "}
                {t("smartRouting.secondary.activeMessage")}
              </p>
            ) : (
              <p className="text-xs text-gray-200">
                {t("smartRouting.secondary.standbyMessage")}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}
